import { centsToDollarsFormatted } from "./currencyHelpers";

// Types for dues order data structure
export type DuesOrder = {
  id: string;
  stage?: string;
  balance: number;
  dueDate?: string;
  payable?: boolean;
  updatedAt: string;
  description?: string;
  status?: string;
  paymentStatus?: string;
  person: {
    id: string;
    pgaId: string;
  };
};

export type DuesOrdersData = {
  duesOrders: {
    nodes: DuesOrder[];
  };
};

// Active order statuses for current dues cycle - using GraphQL enum values
export const ACTIVE_DUES_ORDER_STATUSES = ["DRAFT", "OPEN", "PENDING", "PAID", "CANCELED", "UNCOLLECTIBLE"] as const;

// Helper function to get dues status from an order
export function getDuesStatusFromOrder(order: DuesOrder) {
  const stage = order.stage?.toLowerCase();
  const isOverdue = order.paymentStatus?.toLowerCase() === "overdue";
  const amount = centsToDollarsFormatted(order.balance);
  const duesLabel = "Dues Status (Balance)";

  const statusMap = {
    pending: { label: duesLabel, value: `Pending (${amount})`, variant: "warn" as const },
    draft: { label: duesLabel, value: `Draft (${amount})`, variant: "info" as const },
    paid: { label: duesLabel, value: `Paid (${amount})`, variant: "success" as const },
    canceled: { label: duesLabel, value: `Canceled (${amount})`, variant: undefined },
    uncollectible: { label: duesLabel, value: `Uncollectible (${amount})`, variant: undefined },
    open: isOverdue
      ? { label: duesLabel, value: `Overdue (${amount})`, variant: "error" as const }
      : { label: duesLabel, value: `Open (${amount})`, variant: "warn" as const }
  } as const;

  return statusMap[stage as keyof typeof statusMap] || null;
}

// Helper function to get order priority for sorting
function getOrderPriority(order: DuesOrder) {
  const stage = order.stage?.toLowerCase();
  const isOverdue = order.paymentStatus?.toLowerCase() === "overdue";

  const priorities = {
    pending: 5,
    open: isOverdue ? 4 : 3,
    draft: 2,
    paid: 1,
    canceled: 0,
    uncollectible: 0
  };

  return priorities[stage as keyof typeof priorities] || -1;
}

// Helper function to find dues order that matches the person's balance
export function findMatchingDuesOrder(orders: DuesOrder[], personPgaId: string, duesBalance: number) {
  const personOrders = orders?.filter((order) => order.person.pgaId === personPgaId) || [];

  if (personOrders.length === 0) return undefined;

  // Sort orders by relevance: priority first, then by date, then by balance match
  return personOrders.sort((a, b) => {
    const aPriority = getOrderPriority(a);
    const bPriority = getOrderPriority(b);

    // First: prioritize by order priority (pending > open > draft > paid > canceled)
    if (aPriority !== bPriority) {
      return bPriority - aPriority; // Higher priority first
    }

    // Second: prioritize by most recent due date
    const aDate = new Date(a.dueDate || a.updatedAt).getTime();
    const bDate = new Date(b.dueDate || b.updatedAt).getTime();
    if (aDate !== bDate) {
      return bDate - aDate; // Most recent first
    }

    // Third: prefer exact balance matches as tiebreaker
    const aExactMatch = a.balance === duesBalance ? 1 : 0;
    const bExactMatch = b.balance === duesBalance ? 1 : 0;
    return bExactMatch - aExactMatch;
  })[0];
}

// Helper function to build dues metric card data
export function buildDuesMetric(
  matchingOrder: DuesOrder | undefined,

  pgaId?: string,
  allOrders?: DuesOrder[]
) {
  // If no matching order is found, hide the metric card
  if (!matchingOrder) {
    return null;
  }

  const orderStatus = getDuesStatusFromOrder(matchingOrder);
  if (!orderStatus) {
    return null;
  }

  // Count incomplete orders (not Paid, Canceled, or Uncollectible)
  const incompleteOrdersCount =
    allOrders?.filter((order) => {
      const stage = order.stage?.toLowerCase();
      return stage !== "paid" && stage !== "canceled" && stage !== "uncollectible";
    }).length || 0;

  const duesMetric: any = {
    label: orderStatus.label,
    value: orderStatus.value,
    iconName: "paid",
    variant: orderStatus.variant,
    ...(pgaId && {
      link: {
        to: "/people/:pgaId/billing/orders" as const,
        params: { pgaId }
      }
    }),
    ...(incompleteOrdersCount > 0 && {
      tooltip: {
        supportingText: `${incompleteOrdersCount} Incomplete Dues Order${incompleteOrdersCount === 1 ? "" : "s"}`
      }
    })
  };

  return duesMetric;
}
